// Every coded answer the answer sets draw on.
import type { CodeSystem } from '@medplum/fhirtypes'
import { ASSESSMENT_TEMPLATE_ANSWER_CONCEPTS } from '#src/terminology/assessmentTemplates.ts'
import { INSTRUMENT_ANSWER_CONCEPTS } from '#src/terminology/instruments/instrumentTerminology.ts'
import { PCP_ANSWER_CONCEPTS } from '#src/terminology/pcpInitialAssessment.ts'
import { RD_ANSWER_CONCEPTS } from '#src/terminology/rdInitialAssessment.ts'

export const ASSESSMENT_ANSWER_CODES: CodeSystem = {
  resourceType: 'CodeSystem',
  url: 'https://www.seenhealth.org/fhir/CodeSystem/assessment-answers',
  version: '1',
  name: 'SeenAssessmentAnswers',
  title: 'Assessment Answers',
  status: 'active',
  content: 'complete',
  caseSensitive: true,
  meta: {
    tag: [
      {
        system: 'https://www.seenhealth.org/fhir/managed-by',
        code: 'code',
      },
    ],
  },
  property: [
    {
      code: 'managed-by',
      type: 'code',
    },
  ],
  concept: [
    {
      code: 'oral-med-independent',
      display:
        'Independently and consistently takes correct oral medications at proper dosages at the correct times without any assistance or supervision',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'oral-med-with-setup-or-reminders',
      display:
        'Takes oral medications at correct times if doses are prepared in advance, given daily reminders, or someone develops a drug diary or chart',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'oral-med-independent-sometimes',
      display:
        'Takes oral medications independently some (but not all) of the time',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'oral-med-administered-by-other',
      display:
        'Does not take oral medications unless administered by someone else',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'oral-med-none-prescribed',
      display: 'Not applicable — no orally prescribed medications',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bladder-continent',
      display: 'Continent',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bladder-occasional',
      display: 'Occasional incontinence',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bladder-incontinent',
      display: 'Incontinent',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'yes',
      display: 'Yes',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'no',
      display: 'No',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'none-needed',
      display: 'None needed',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'interpreter-organization',
      display: 'Vendor or service',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'interpreter-practitioner',
      display: 'Identified interpreter',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'interpreter-related-person',
      display: 'Family or caregiver',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'in-person',
      display: 'In Person',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'phone-mode',
      display: 'Over the Phone',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'telehealth',
      display: 'Telehealth',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'rehab-gym',
      display: 'Rehab Gym',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'day-center',
      display: 'Day Center',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'ppt-home',
      display: 'PPT’s Home',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'mandarin',
      display: 'Mandarin',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'cantonese',
      display: 'Cantonese',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'english',
      display: 'English',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'floor-none',
      display: 'No flooring/carpeting hazards',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'holes-in-floor',
      display: 'Holes in floor',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'wires-in-walkways',
      display: 'Electric wires in walkways',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'loose-rugs-cords',
      display: 'Loose rugs, cords, or tubes',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'other-specify',
      display: 'Other, please specify:',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bath-none',
      display: 'No bathroom/toilet hazards',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'toilet-faucets',
      display: 'Non-operating toilet or faucets',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'leaking-pipes',
      display: 'Leaking pipes',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'no-rails',
      display: 'No rails, though needed',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'slippery-bathtub',
      display: 'Slippery bathtub',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'outside-toilet',
      display: 'Outside toilet',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bath-distance',
      display: 'Distance from bedroom to bathroom far',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'lack-of-warm-water',
      display: 'Lack of warm water',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bed-none',
      display: 'No bedroom hazards',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bed-height',
      display: 'Bed height (e.g., too high or low)',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'blankets',
      display: 'Blankets hanging off bed',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'loose-rugs',
      display: 'Loose rugs',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'hard-clothes',
      display: 'Hard to reach clothes',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'kitchen-none',
      display: 'No kitchen hazards',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'dangerous-stove',
      display: 'Dangerous stove',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'kitchen-infestation',
      display: 'Infestation by rats or bugs',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'cold-none',
      display: 'No refrigeration issues',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'no-refrigerator',
      display: 'Does not have a working refrigerator on site',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'poor-temperature',
      display: 'Poor temperature control',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'food-space',
      display: 'Insufficient space for food storage',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'expired-food',
      display: 'Foul smelling/ expired foods',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'food-insecurity',
      display: 'Does not have sufficient supply of foods (food insecurity)',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'heat-none',
      display: 'No heating/cooling hazards',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'too-hot',
      display: 'Too hot in summer',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'too-cold',
      display: 'Too cold in winter',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'heating-equipment',
      display: 'Potential dangers with heating equipment',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'safety-none',
      display: 'No personal safety issues',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'fear-of-violence',
      display: 'Fear of violence',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'mailbox',
      display: 'Going to the mailbox',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'neighbors',
      display: 'Visiting neighbors',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'street-traffic',
      display: 'Heavy street traffic',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'weapons',
      display: 'Weapons in home',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'no-deadbolt',
      display: 'No deadbolt locks',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'access-none',
      display: 'No home access issues',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'entry',
      display: 'Difficulty entering/leaving home',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'egress',
      display: 'Inadequate egress',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'walkways',
      display: 'Walkways not cleared/need repair',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'pet-none',
      display: 'No pet issues',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'pet-care',
      display: 'Difficulty caring for pet(s)',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'pet-fall',
      display: 'Pet(s) pose fall risk',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bug-none',
      display: 'No home bug infestations',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bed-bugs-now',
      display: 'Bed bugs currently',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bed-bugs-history',
      display: 'Bed bugs history',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'rodents',
      display: 'Rodent infestation requiring treatment',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'decision-independent',
      display: 'Independent – decisions consistently reasonable',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'decision-modified',
      display: 'Modified independence – some difficult in new situations only',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'decision-minimal',
      display:
        'Minimally impaired – in specific situations, decisions become poor or unsafe and cues/supervision necessary at those times',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'decision-moderate',
      display:
        'Moderately impaired – decisions consistently poor or unsafe and cues/supervision required at all times',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'decision-severe',
      display: 'Severely impaired – never/rarely made decisions',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bladder-continent-no-device',
      display:
        'Continent – complete control; does not use any type of catheter or other urinary collection devices',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bladder-occasional-not-daily',
      display:
        'Occasionally incontinent – incontinent episodes 2 or more times week, but not daily',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bladder-incontinent-daily',
      display: 'Incontinent – inadequate control, multiple daily episodes',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bladder-device',
      display: 'Requires catheter or urinary device collection',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bowel-continent',
      display: 'Continent',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bowel-occasional',
      display: 'Occasionally incontinent',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'bowel-incontinent',
      display: 'Incontinent',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'oral-independent',
      display:
        'Independently and consistently takes correct oral medications at proper dosages at the correct times without any assistance or supervision',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'oral-setup-or-reminders',
      display:
        'Takes oral medications at correct times if (1) individual dosages are prepared in advance by another person OR (2) given daily reminders OR (3) someone develops a drug diary or chart',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'oral-administered',
      display:
        'Does not take oral medications unless administered by someone else',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'oral-none',
      display: 'Not applicable - no orally prescribed medications',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'barrier-visual',
      display: 'Visual',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'barrier-hearing',
      display: 'Hearing',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'barrier-read',
      display: 'Unable to read',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'barrier-comprehend',
      display: 'Unable to comprehend what is read',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'barrier-other',
      display: 'Other',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'barrier-none',
      display: 'None',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'educated-participant',
      display: 'Participant',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'educated-family',
      display: 'Family member',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'educated-caregiver',
      display: 'Caregiver (if other than family)',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'learn-participant-verbal',
      display: 'Participant verbalized understanding',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'learn-family-verbal',
      display: 'Family member verbalized understanding',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'learn-caregiver-verbal',
      display: 'Caregiver verbalized understanding',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'learn-participant-demo',
      display: 'Participant demonstrated correctly',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'learn-family-demo',
      display: 'Family member demonstrated correctly',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'learn-caregiver-demo',
      display: 'Caregiver demonstrated correctly',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    {
      code: 'learn-unsuccessful',
      display: 'Demonstration unsuccessful',
      property: [
        {
          code: 'managed-by',
          valueCode: 'code',
        },
      ],
    },
    ...PCP_ANSWER_CONCEPTS,
    ...RD_ANSWER_CONCEPTS,
    ...ASSESSMENT_TEMPLATE_ANSWER_CONCEPTS,
    ...INSTRUMENT_ANSWER_CONCEPTS,
  ],
}
